import React from "react";
import { toRouterPath, toEmbedUrl } from "./AssistantLinkCard";

/**
 * Minimal markdown-lite renderer for assistant replies: headings, bold, bullet and
 * numbered lists, inline code, horizontal rules, line breaks and `[label](url)` links.
 * Gemini's text responses commonly use `**bold**`, `* item` bullets, `### Заголовок`
 * and — when it forgets to use the open_page tool — inline markdown links with a
 * (sometimes hallucinated) domain. Links are made clickable and routed client-side to the
 * path only, so a wrong domain is ignored. Intentionally not a full markdown parser:
 * everything is built from React elements, never `dangerouslySetInnerHTML`, so there is
 * no XSS surface. Images, raw HTML and tables are deliberately not supported.
 *
 * `onNavigate(routerPath)` is called on link click for client-side navigation.
 */
export default function formatAssistantText(text, onNavigate) {
  if (!text) return null;
  const lines = stripLatex(String(text)).split("\n");
  const blocks = [];
  let listBuffer = [];
  let listOrdered = false;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const Tag = listOrdered ? "ol" : "ul";
    blocks.push(
      <Tag key={`list-${blocks.length}`} style={styles.list}>
        {listBuffer.map((item, idx) => (
          <li key={idx}>{renderInline(item, onNavigate)}</li>
        ))}
      </Tag>
    );
    listBuffer = [];
  };

  lines.forEach((line, idx) => {
    // `* item` / `- item` — маркированный, `1. item` — нумерованный.
    const bulletMatch = /^\s*[*-]\s+(.*)$/.exec(line);
    const orderedMatch = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bulletMatch || orderedMatch) {
      const ordered = Boolean(orderedMatch);
      // Смена типа списка закрывает предыдущий, иначе пункты слипнутся в один.
      if (listBuffer.length > 0 && ordered !== listOrdered) flushList();
      listOrdered = ordered;
      listBuffer.push((bulletMatch || orderedMatch)[1]);
      return;
    }
    flushList();

    const headingMatch = /^\s*(#{1,6})\s+(.*)$/.exec(line);
    if (headingMatch) {
      // Не <h1>–<h6>: виджет узкий, а заголовки страницы должны оставаться
      // уникальными для доступности — вкрапления из ответа бота их ломают.
      const level = Math.min(headingMatch[1].length, 3);
      blocks.push(
        <div key={`h-${idx}`} style={{ ...styles.heading, ...headingSizes[level] }}>
          {renderInline(headingMatch[2], onNavigate)}
        </div>
      );
      return;
    }

    if (/^\s*(---+|\*\*\*+|___+)\s*$/.test(line)) {
      blocks.push(<hr key={`hr-${idx}`} style={styles.rule} />);
      return;
    }

    if (line.trim() === "") {
      blocks.push(<br key={`br-${idx}`} />);
    } else {
      blocks.push(<div key={`line-${idx}`}>{renderInline(line, onNavigate)}</div>);
    }
  });
  flushList();

  return blocks;
}

/**
 * Разворачивает LaTeX в читаемый текст.
 *
 * Формулы модель выдаёт от случая к случаю, и ставить ради них KaTeX (~300 КБ плюс
 * свой CSS) несоразмерно. Убираем обёртку `$$…$$` и `\text{…}`, оставляя содержимое —
 * формула превращается в обычную строку. Основную работу делает запрет в системном
 * промпте; это страховка на случай, когда модель всё равно вернёт LaTeX.
 */
export function stripLatex(text) {
  return (
    text
      // $$...$$ и \[...\] — блочные формулы, могут быть многострочными.
      .replace(/\$\$([\s\S]*?)\$\$/g, (_, body) => unwrapLatexBody(body))
      .replace(/\\\[([\s\S]*?)\\\]/g, (_, body) => unwrapLatexBody(body))
      // $...$ и \(...\) — инлайновые. Осторожно: одиночный $ рядом с числом
      // (цена в долларах) не трогаем. Без lookbehind: он валит парсинг бандла в старых
      // Safari, а browserslist здесь ">0.2%, not dead" — такие версии в выборку попадают.
      .replace(/\$(\S(?:[^$\n]*\S)?)\$/g, (_, body) => unwrapLatexBody(body))
      .replace(/\\\(([\s\S]*?)\\\)/g, (_, body) => unwrapLatexBody(body))
  );
}

function unwrapLatexBody(body) {
  return body
    .replace(/\\(?:text|mathrm|textbf|mathbf|operatorname)\s*\{([^{}]*)\}/g, "$1")
    .replace(/\\times/g, "×")
    .replace(/\\cdot/g, "·")
    .replace(/\\div/g, "÷")
    .replace(/\\approx/g, "≈")
    .replace(/\\le/g, "≤")
    .replace(/\\ge/g, "≥")
    .replace(/\\%/g, "%")
    .replace(/\\,|\\;|\\!/g, " ")
    .replace(/\\\\/g, " ")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function renderBold(segment, keyPrefix) {
  return segment
    .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
    .filter(Boolean)
    .map((part, idx) => {
      const boldMatch = /^\*\*([^*]+)\*\*$/.exec(part);
      if (boldMatch) return <strong key={`${keyPrefix}-b${idx}`}>{boldMatch[1]}</strong>;
      const codeMatch = /^`([^`]+)`$/.exec(part);
      if (codeMatch)
        return (
          <code key={`${keyPrefix}-c${idx}`} style={styles.code}>
            {codeMatch[1]}
          </code>
        );
      return <React.Fragment key={`${keyPrefix}-t${idx}`}>{part}</React.Fragment>;
    });
}

function renderInline(line, onNavigate) {
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  const nodes = [];
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = linkRe.exec(line)) !== null) {
    if (m.index > lastIndex) {
      nodes.push(...renderBold(line.slice(lastIndex, m.index), `s${key++}`));
    }
    const to = toRouterPath(m[2]);
    nodes.push(
      <a
        key={`lnk${key++}`}
        href={toEmbedUrl(to)}
        style={styles.link}
        onClick={(e) => {
          e.preventDefault();
          if (onNavigate) onNavigate(to);
        }}
      >
        {m[1]}
      </a>
    );
    lastIndex = linkRe.lastIndex;
  }
  if (lastIndex < line.length) {
    nodes.push(...renderBold(line.slice(lastIndex), `s${key++}`));
  }
  return nodes;
}

const headingSizes = {
  1: { fontSize: 16 },
  2: { fontSize: 15 },
  3: { fontSize: 14 },
};

const styles = {
  list: { margin: "4px 0", paddingLeft: 18 },
  link: { color: "var(--c-accent)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" },
  heading: { fontWeight: 700, margin: "10px 0 4px" },
  rule: { border: 0, borderTop: "1px solid var(--c-border)", margin: "10px 0" },
  code: {
    padding: "1px 5px",
    borderRadius: 4,
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    fontSize: "0.92em",
  },
};
